import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Logger from 'observability/Logger';
import { useBootstrap } from 'utils/bootstrap/useBootstrap';

import GoldcastMessage from './GoldcastMessage';

const LOG = new Logger('GoldcastEmbed');

const LOAD_STATE = {
    LOADING: 'loading',
    READY: 'ready',
    ERROR: 'error',
};

const EmbedWrapper = styled.div`
    width: 100%;
    height: 100%;
`;

let goldcastSdkPromise;

const loadGoldcastSdk = () => {
    if (window?.GOLDCAST?.RenderEmbeddedWidget) {
        return Promise.resolve();
    }

    if (goldcastSdkPromise) {
        return goldcastSdkPromise;
    }

    goldcastSdkPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://embedded.sdk.goldcast.io/embedded-sdk.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = (error) => {
            script.remove();
            goldcastSdkPromise = null;
            reject(error);
        };

        document.body.appendChild(script);
    });

    return goldcastSdkPromise;
};

const GoldcastEmbed = ({ config }) => {
    const initializedRef = React.useRef(false);
    const { goldcastEmbeddedServerUrl } = useBootstrap(['goldcastEmbeddedServerUrl']);
    const [loadState, setLoadState] = React.useState(LOAD_STATE.LOADING);

    React.useEffect(() => {
        if (!goldcastEmbeddedServerUrl || !config || initializedRef.current) {
            return undefined;
        }

        const { eventId, attendee } = config;
        if (!eventId || !attendee) {
            setLoadState(LOAD_STATE.ERROR);
            return undefined;
        }

        let isMounted = true;

        loadGoldcastSdk()
            .then(() => {
                if (!isMounted || initializedRef.current) {
                    return;
                }

                const widgetConfig = {
                    embeddedServerUrl: goldcastEmbeddedServerUrl,
                    eventID: eventId,
                    ...attendee,
                };
                const widget = new window.GOLDCAST.RenderEmbeddedWidget(widgetConfig);
                widget.init();
                initializedRef.current = true;
                setLoadState(LOAD_STATE.READY);
            })
            .catch((error) => {
                LOG.error('Goldcast embed SDK failed to load', error);
                if (isMounted) {
                    setLoadState(LOAD_STATE.ERROR);
                }
            });

        return () => {
            isMounted = false;
            initializedRef.current = false;
        };
    }, [goldcastEmbeddedServerUrl, config]);

    if (
        !goldcastEmbeddedServerUrl ||
        !config ||
        !config?.eventId ||
        !config?.attendee ||
        loadState === LOAD_STATE.ERROR
    ) {
        return <GoldcastMessage message="Something went wrong. Please try again later." />;
    }

    return <EmbedWrapper id="gc-embedded-div" />;
};

GoldcastEmbed.propTypes = {
    config: PropTypes.shape({
        eventId: PropTypes.string,
        attendee: PropTypes.shape({
            shortId: PropTypes.string,
            secretCode: PropTypes.string,
            emailId: PropTypes.string,
        }),
        attendeeCreationRequired: PropTypes.bool,
    }),
};

GoldcastEmbed.defaultProps = {
    config: null,
};

export default GoldcastEmbed;
